import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { formatDate } from '../../../utils/appDateTime';

function money(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function CrmPayrollProcessing() {
  const { toast } = useToast();
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [department, setDepartment] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [preview, setPreview] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [empRes, runRes] = await Promise.all([
        api.get('/crm/employees'),
        api.get('/crm/payroll/runs'),
      ]);
      setEmployees((empRes.data?.data?.items || []).filter((e) => e.status === 'active'));
      setRuns(runRes.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))];

  const toggleEmp = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post('/crm/payroll/runs/preview', {
        employee_ids: selected,
        department: department || undefined,
        period_start: periodStart || undefined,
        period_end: periodEnd || undefined,
      });
      setPreview(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const createRun = async () => {
    if (!periodStart || !periodEnd) {
      toast({ title: 'Period required', description: 'Set period start and end', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/crm/payroll/runs', {
        employee_ids: selected,
        department: department || undefined,
        period_start: periodStart,
        period_end: periodEnd,
        period_label: `${periodStart} to ${periodEnd}`,
      });
      toast({ title: 'Created', description: `${res.data?.data?.created || 0} salary rows` });
      setPreview([]);
      load();
      if (res.data?.data?.run?.id) openRun(res.data.data.run.id);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const generateDue = async () => {
    setBusy(true);
    try {
      const res = await api.post('/crm/payroll/runs/generate-due', {});
      toast({ title: 'Generated', description: `${res.data?.data?.generated || 0} records` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const openRun = async (id) => {
    try {
      const res = await api.get(`/crm/payroll/runs/${id}`);
      setActiveRun(res.data?.data || null);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const transition = async (action) => {
    if (!activeRun?.run?.id) return;
    setBusy(true);
    try {
      await api.post(`/crm/payroll/runs/${activeRun.run.id}/${action}`, {});
      toast({ title: 'Updated', description: action });
      openRun(activeRun.run.id);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <Button onClick={generateDue} disabled={busy}>Generate Due Payrolls</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Bulk Payroll Run</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Period Start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </div>
            <div>
              <Label>Department</Label>
              <Select value={department || 'all'} onValueChange={(v) => setDepartment(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="max-h-40 overflow-y-auto border rounded-md p-2 grid sm:grid-cols-2 gap-1">
            {employees
              .filter((e) => !department || e.department === department)
              .map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(e.id)}
                    onChange={() => toggleEmp(e.id)}
                  />
                  {e.first_name} {e.last_name} ({e.employee_code})
                </label>
              ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={busy} onClick={doPreview}>Preview</Button>
            <Button disabled={busy} onClick={createRun}>Create Run (Draft)</Button>
          </div>

          {preview.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2">Employee</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p, i) => {
                    if (p.error) {
                      return (
                        <tr key={i} className="border-b">
                          <td className="py-2">{p.employee?.employee_code}</td>
                          <td colSpan={3} className="text-destructive">{p.error}</td>
                        </tr>
                      );
                    }
                    const calc = p.calculation || {};
                    const name = `${p.employee?.first_name || ''} ${p.employee?.last_name || ''}`.trim();
                    return (
                      <tr key={i} className="border-b">
                        <td className="py-2">{name}</td>
                        <td>{money(calc.gross_salary)}</td>
                        <td>{money(calc.total_deductions)}</td>
                        <td className="font-medium">{money(calc.net_salary)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Runs</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {runs.map((r) => (
              <button
                key={r.id}
                type="button"
                className="w-full text-left border rounded-md px-3 py-2 text-sm hover:bg-muted"
                onClick={() => openRun(r.id)}
              >
                <div className="font-medium">#{r.id} · {r.period_label}</div>
                <div className="text-xs text-muted-foreground">{r.status} · {formatDate(r.created_at)}</div>
              </button>
            ))}
            {runs.length === 0 && <p className="text-sm text-muted-foreground">No runs yet</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Run Detail</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!activeRun ? (
              <p className="text-sm text-muted-foreground">Select a run</p>
            ) : (
              <>
                <div className="text-sm">
                  <div className="font-medium">{activeRun.run.period_label}</div>
                  <div className="text-muted-foreground">Status: {activeRun.run.status}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={busy} onClick={() => transition('calculate')}>Calculate</Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => transition('submit-approval')}>Submit Approval</Button>
                  <Button size="sm" disabled={busy} onClick={() => transition('approve')}>Approve</Button>
                  <Button size="sm" disabled={busy} onClick={() => transition('mark-paid')}>Mark Paid</Button>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1">Employee</th>
                        <th>Net</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(activeRun.salaries || []).map((s) => (
                        <tr key={s.id} className="border-b">
                          <td className="py-1">{s.first_name} {s.last_name}</td>
                          <td>{money(s.net_salary || s.final_salary)}</td>
                          <td>{s.workflow_status || s.payment_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
